import { useAuth } from '@clerk/react-router';
import { useEffect } from 'react';
import { UserContextV2 } from '~/app';
import { resetSatelliteSyncAttempts } from '~/pages/authentication/satelliteSyncGuard';
import { authDebug } from './authDebug';

/**
 * Auth State Machine Connector
 *
 * Connects Clerk auth state to the XState userState machine.
 *
 * Gated on `isLoaded`: during Clerk's initial hydration `isSignedIn` is
 * `undefined` (so `!isSignedIn` is truthy) and `userId` is `null`. Acting on
 * either value before Clerk has settled produced two failure modes —
 * spurious `ON_USER_LOGOUT` events fired while signed-in sessions were
 * restoring, and the post-sign-in user fetch racing Clerk's token
 * availability so the dashboard hung on an infinite spinner until the user
 * refreshed. Waiting for `isLoaded` collapses both.
 */
export const AuthStateMachine = () => {
  const { isLoaded, userId, isSignedIn } = useAuth();
  const actorRef = UserContextV2.useActorRef();

  useEffect(() => {
    if (!isLoaded) {
      authDebug('clerk.state.loading', {
        isLoaded,
        isSignedIn,
        hasUserId: Boolean(userId),
      });
      return;
    }

    if (isSignedIn && userId) {
      authDebug('clerk.state.login', {
        isLoaded,
        isSignedIn,
        hasUserId: true,
      });
      // The satellite session settled — clear the sync-loop breaker so a later
      // sign-out/in isn't held down by a stale bounce count.
      resetSatelliteSyncAttempts();
      actorRef.send({ type: 'ON_USER_LOGIN' });
      actorRef.send({ type: 'ON_SET_USER_ID', userId });
    } else if (!isSignedIn) {
      authDebug('clerk.state.logout', {
        isLoaded,
        isSignedIn,
        hasUserId: Boolean(userId),
      });
      actorRef.send({ type: 'ON_USER_LOGOUT' });
    }
  }, [isLoaded, isSignedIn, userId, actorRef]);

  return null;
};
